/**
 * useConfigVersionRevalidator
 *
 * 监听 React Router 路由变化和切回前台（visibilitychange），节流 5 分钟最多触发一次 `GET /system/config/versions`。
 * 覆盖"用户挂着 tab 不刷新"的盲区 — 即便长开 tab，路由切换时也会拿到最新版本号。
 *
 * 失败不阻塞 UI（console.warn）。
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { configVersionsApi } from '@/api/configVersionsApi';
import { useAppDispatch } from '@/hooks/redux';
import { applyServerVersions } from '@/store/slices/lookupSlice';
import { loadUserMenus } from '@/store/slices/authSlice';
import { menuRelevantVersionsChanged } from '@/hooks/menuVersionsChanged';
import { getCacheStore } from '@/utils/cacheStore';
import { emitResourceVersionChanges } from '@/utils/configVersionEvents';

const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes
const META_KEY_VERSIONS = 'versions';

export function useConfigVersionRevalidator(enabled: boolean = true): void {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const lastCheckRef = useRef<number>(0);

  // 节流 5 分钟；换路由、切回前台都走它（合同：bootstrap、切回前台、换路由时读版本号）
  const check = useCallback(() => {
    if (!enabled) return;
    const now = Date.now();
    if (now - lastCheckRef.current < THROTTLE_MS) return;
    lastCheckRef.current = now;

    void (async () => {
      try {
        const serverVersions = await configVersionsApi.getVersions();
        const store = getCacheStore();
        const prevVersions = (await store.get<Record<string, string>>('meta', META_KEY_VERSIONS)) || {};
        dispatch(applyServerVersions({ prev: prevVersions, next: serverVersions }));
        // 不在 Redux 里的模块级缓存（表单目录、种类表单）也按配置版本失效
        emitResourceVersionChanges(prevVersions, serverVersions);
        if (menuRelevantVersionsChanged(prevVersions, serverVersions)) {
          void dispatch(loadUserMenus());
        }
        await store.set('meta', META_KEY_VERSIONS, serverVersions);
      } catch (err) {
        console.warn('[revalidator] /system/config/versions failed', err);
        lastCheckRef.current = 0;
      }
    })();
  }, [enabled, dispatch]);

  useEffect(() => {
    check();
  }, [check, location.pathname]);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, check]);
}
